import filter from 'lodash/fp/filter'
import flow from 'lodash/fp/flow'
import map from 'lodash/fp/map'
import sortBy from 'lodash/fp/sortBy'
import { IShipmentIssueTypeSlaBasedOn } from 'types/graphqlTypes'
import { IIssueResolutionSteps, ShipmentIssueType } from 'apps/marketplace/apiTypes/shipmentIssues'
import { IRoleOption } from '../hooks/useAssigneeRoles'

const ASSIGNEE_ROLE = 'ShipmentIssueAssigneeRole'

const isAssigneeRole = (type?: string) => type === ASSIGNEE_ROLE

export const ISSUE_TYPE_DEFAULTS = {
  name: '',
  description: '',
  shipmentIssueCategoryId: undefined,
  defaultPriority: 4,
  shipmentIssueTypeDefaultSubscribers: [],
  shipmentIssueTypeDefaultOwner: '',
  otherOwner: {},
  otherAssigneesList: [],
  slaDuration: 10 * 60 * 60, // default is 10 hours in seconds
  slaBasedOn: IShipmentIssueTypeSlaBasedOn.issue_creation,
}

export const getDefaultValuesFromIssue = (issueType?: ShipmentIssueType) => {
  if (!issueType) {
    return {}
  }

  const {
    defaultPriority: currentPriority,
    description: currentDescription,
    issueResolutionSteps: currentIssueResolutionSteps,
    shipmentIssueTypeDefaultOwner: currentOwner,
    shipmentIssueTypeDefaultSubscribers: currentSubscribers,
    slaBasedOn: currentSlaBasedOn,
    slaDuration: currentSlaDuration,
    stepsToResolve: currentStepsToResolve,
  } = issueType

  const defaultSubscribersCheckboxes: { [key: string]: Boolean } = {}

  currentSubscribers?.forEach(({ subscriberId, subscriberType }) => {
    if (isAssigneeRole(subscriberType)) {
      defaultSubscribersCheckboxes[`${subscriberType}-${subscriberId}`] = true
    }
  })

  const shipmentIssueTypeDefaultSubscribers: IRoleOption[] =
    currentSubscribers
      ?.filter(subscriber => !isAssigneeRole(subscriber?.subscriberType))
      .map(sub => {
        const { firstName, lastName, subscriberId, subscriberType } = sub
        const subFullName = `${firstName} ${lastName}`
        return {
          id: String(subscriberId),
          value: String(subscriberId),
          type: subscriberType,
          label: subFullName,
          text: subFullName,
        }
      }) || []

  const otherAssigneesList = [...(shipmentIssueTypeDefaultSubscribers || [])]

  let otherOwner: IRoleOption = {} as IRoleOption
  let shipmentIssueTypeDefaultOwner = ''

  if (currentOwner) {
    shipmentIssueTypeDefaultOwner = String(currentOwner?.ownerId) || shipmentIssueTypeDefaultOwner

    if (!isAssigneeRole(currentOwner?.ownerType)) {
      const { firstName, lastName, ownerId, ownerType } = currentOwner
      const ownerFullName = `${firstName} ${lastName}`
      otherOwner = {
        id: String(ownerId),
        value: String(ownerId),
        type: ownerType,
        label: ownerFullName,
        text: ownerFullName,
      }

      shipmentIssueTypeDefaultOwner = String(ownerId)

      if (
        !otherAssigneesList.find(
          assignee => assignee.id === otherOwner.id && assignee.type === otherOwner.type
        )
      ) {
        otherAssigneesList.push(otherOwner)
      }
    }
  }

  const stepsToResolve = currentStepsToResolve?.map(item => ({ name: item }))
  const issueResolutionSteps = currentIssueResolutionSteps?.length
    ? flow(
        filter((item: IIssueResolutionSteps) => item.deletedAt === null),
        sortBy('stepSequence'),
        map((item: IIssueResolutionSteps) => ({ ...item, itemId: item.id }))
      )(currentIssueResolutionSteps)
    : currentStepsToResolve?.map((item, index) => ({
        description: item,
        stepSequence: index + 1,
      }))

  return {
    ...issueType,
    ...defaultSubscribersCheckboxes,
    shipmentIssueTypeDefaultSubscribers,
    description: currentDescription || '',
    otherOwner,
    shipmentIssueTypeDefaultOwner,
    otherAssigneesList,
    stepsToResolve,
    issueResolutionSteps,
    slaDuration: currentSlaDuration ?? ISSUE_TYPE_DEFAULTS.slaDuration,
    slaBasedOn: currentSlaBasedOn || ISSUE_TYPE_DEFAULTS.slaBasedOn,
    defaultPriority: currentPriority || ISSUE_TYPE_DEFAULTS.defaultPriority,
  }
}
